/**
 * Legacy UI Config
 *
 * When the Legacy UI is active, the widget's style and copy come from the legacy
 * V1 config fields (`theme`, `customText`, `ongoingChat`, `closeButton`,
 * `showPoweredByGladly`, `privacyDisclaimer`) rather than `v2.appearance` — the
 * same fields the real V1 widget reads. These helpers normalize those fields,
 * mirroring V1's theme defaults and its `getRadius` scale, so components/CSS get
 * V1-faithful values live from whatever config is loaded.
 */

import {
  BORDER_RADII,
  type BorderRadius,
  CLOSE_ICON_TYPE,
  type CloseIconType,
  type Theme,
} from '@/domain/configuration';
import { colorToRgbaOr, darkenColor, getVisibleColor, readableColor, readableIconColor } from '@/lib/colorUtils';

/**
 * Structural view of the server config the legacy-UI toggle reads. Tolerant of a
 * missing config so callers can pass a possibly-null server config directly.
 */
export type LegacyUISource = { v2?: { legacyUI?: boolean } } | null | undefined;

/**
 * True when the V2 widget should render the legacy (V1) look. Driven by the V2
 * server-config field `v2.legacyUI` — the same runtime, restyled to match the
 * legacy V1 widget. There is no self-service pane or onboarding flow (neither
 * exists in V2), so it is purely the V1 chat experience.
 */
export function isLegacyUIEnabled(server: LegacyUISource): boolean {
  return server?.v2?.legacyUI === true;
}

/**
 * The subset of the loaded (V1-shaped) config these helpers read. Structural
 * and optional so the full ServerConfiguration satisfies it and tests can pass
 * minimal objects.
 */
export interface LegacyUIConfigSource {
  theme?: Theme;
  borderRadius?: BorderRadius;
  customText?: { header?: { greeting?: string; title?: string } };
  ongoingChat?: { title?: string };
  closeButton?: { iconType?: CloseIconType };
  showPoweredByGladly?: boolean;
}

// Mirrors web/src/reducers/initialStates/configuration.js theme defaults.
const LEGACY_DEFAULTS = {
  alert: '#FE1A20',
  brandPrimary: '#f3f3f3',
  brandSecondary: '#ffffff',
  greetingCloseButton: '#000000',
  font: "'Helvetica Neue', Helvetica, sans-serif",
  borderRadius: BORDER_RADII.LARGE,
  dropshadowEnabled: true,
} as const;

// Mirrors web/src/lib/getRadius.js: NONE -> 0, SMALL -> smallPx, LARGE -> largePx.
function getRadius(radius: BorderRadius, smallPx: number, largePx: number): number {
  if (radius === BORDER_RADII.LARGE) return largePx;
  if (radius === BORDER_RADII.SMALL) return smallPx;
  return 0;
}

export interface LegacyUITheme {
  colors: {
    agentBubble: string; // V1 brandPrimary — in-panel brand surfaces + typing indicator
    agentBubbleBorder: string; // subtle border derived from the agent bubble (V1: darken 6%)
    agentBubbleText: string; // auto-contrast text on the agent bubble (V1: determineFontColor)
    consumerBubble: string; // V1 brandSecondary — consumer message bubble
    consumerBubbleText: string; // auto-contrast text on the consumer bubble
    consumerBubbleBorder: string; // subtle border derived from the consumer bubble (V1: darken 6%)
    launcher: string; // V1 launcher bg = brandAccent ?? brandPrimary
    quickReplyBorder: string; // quick reply button border, guarded against white
    quickReplyHover: string; // quick reply button hover/focus tint (brand at 10%)
    sendButton: string; // active send button fill: the Basic Accent (chatLauncher ?? brandPrimary)
    sendButtonIcon: string; // auto-contrast send arrow on that fill
    alert: string; // unread dot
    greetingCloseButton: string;
  };
  fonts: { primary: string; secondary: string };
  dropshadowEnabled: boolean;
  // Per-element radii in px, from V1's getRadius scale for the config's borderRadius.
  radius: { launcher: number; panel: number; bubble: number; input: number };
}

export function getLegacyUITheme(server: LegacyUIConfigSource): LegacyUITheme {
  const theme = server.theme ?? {};
  const colors = theme.colors ?? {};
  // V1's config reducer overwrites theme.borderRadius with the top-level
  // borderRadius when present (web/ configuration.js handleConfigUpdate), so the
  // top-level value wins.
  const radius = server.borderRadius ?? theme.borderRadius ?? LEGACY_DEFAULTS.borderRadius;
  const brandPrimary = colors.brandPrimary ?? LEGACY_DEFAULTS.brandPrimary;
  const consumerBubble = colors.brandSecondary ?? LEGACY_DEFAULTS.brandSecondary;
  // Admin's Basic form edits only chatLauncher ("Accent color"), not brandPrimary,
  // so the send button reads the same source as the quick replies [sc-263283].
  const sendButton = colors.chatLauncher ?? brandPrimary;

  return {
    colors: {
      agentBubble: brandPrimary,
      agentBubbleBorder: darkenColor(brandPrimary, 6),
      agentBubbleText: readableColor(brandPrimary),
      consumerBubble,
      consumerBubbleText: readableColor(consumerBubble),
      consumerBubbleBorder: darkenColor(consumerBubble, 6),
      // V1's launcher bg = brandPrimary, overridden by brandAccent when set
      // (Movado sets brandAccent #FFFFFF → white bubble). Icon auto-contrasts.
      launcher: colors.brandAccent ?? brandPrimary,
      // V1's quick reply buttons take their border and hover tint from
      // chatLauncher, falling back to brandPrimary (web/ QuickReplyButton.jsx) —
      // note that is chatLauncher, not the launcher bubble's brandAccent.
      quickReplyBorder: getVisibleColor(colors.chatLauncher ?? brandPrimary, '#e3e3e3'),
      // Falls back rather than emitting rgba(NaN, …) for a named or rgb() brand
      // colour: the var would still be *defined*, so the stylesheet's own
      // fallback could never apply and the hover tint would compute to
      // transparent instead of neutral.
      quickReplyHover: colorToRgbaOr(colors.chatLauncher ?? brandPrimary, 0.1, 'rgba(0, 0, 0, 0.05)'),
      sendButton,
      sendButtonIcon: readableIconColor(sendButton),
      alert: colors.alert ?? LEGACY_DEFAULTS.alert,
      greetingCloseButton: colors.greetingCloseButton ?? LEGACY_DEFAULTS.greetingCloseButton,
    },
    fonts: {
      primary: theme.fontLibrary?.primary ?? LEGACY_DEFAULTS.font,
      secondary: theme.fontLibrary?.secondary ?? LEGACY_DEFAULTS.font,
    },
    dropshadowEnabled: theme.dropshadowEnabled ?? LEGACY_DEFAULTS.dropshadowEnabled,
    radius: {
      launcher: getRadius(radius, 4, 32), // V1 buttons
      panel: getRadius(radius, 8, 16), // V1 panel
      bubble: getRadius(radius, 8, 16), // V1 message bubbles
      input: getRadius(radius, 2, 4), // V1 inputs
    },
  };
}

export interface LegacyUICopy {
  headerTitle: string;
  closeIconType: CloseIconType;
  showPoweredByGladly: boolean;
}

export function getLegacyUICopy(server: LegacyUIConfigSource): LegacyUICopy {
  return {
    // V1's chat header shows the greeting, falling back to the ongoing-chat title.
    headerTitle: server.customText?.header?.greeting || server.ongoingChat?.title || '',
    closeIconType: server.closeButton?.iconType ?? CLOSE_ICON_TYPE.X_ICON,
    showPoweredByGladly: server.showPoweredByGladly ?? false,
  };
}

/**
 * Single source of truth for whether "Powered by Gladly" should render, reading
 * the correct field for the active mode: Legacy UI takes it from the V1 config
 * (via getLegacyUICopy), V2 from `v2.appearance.showPoweredByGladly`. Footer and
 * WidgetContainer both consume this and pick placement by isLegacyUIEnabled, so
 * the branding can never render twice or go missing as the config changes.
 */
export function isPoweredByGladlyVisible(
  server: LegacyUIConfigSource & { v2?: { legacyUI?: boolean; appearance?: { showPoweredByGladly?: boolean } } },
): boolean {
  return isLegacyUIEnabled(server)
    ? getLegacyUICopy(server).showPoweredByGladly
    : (server.v2?.appearance?.showPoweredByGladly ?? false);
}

/**
 * V1-style per-message timestamp, e.g. "2:16 pm" — a format V2 doesn't otherwise
 * show. The locale is pinned to 'en-US' so the format stays stable regardless of
 * the host locale. Returns '' for an unparseable date.
 */
export function formatV1Time(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // Modern ICU (Chrome/Safari, ICU >= 72) separates the time from the day period
  // with a narrow no-break space (U+202F); normalize whitespace to a plain space
  // so the rendered stamp — and its tests — stay deterministic across engines.
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s+/g, ' ').toLowerCase();
}
